import { LitElement, html, css } from 'lit';
import { unsafeSVG } from 'lit/directives/unsafe-svg.js';
import { Edit2, Info } from 'lucide';
import { lucideIcon } from '../icon.js';
import { api } from '../api.js';
import { tipMove, tipHide } from '../utils/hover-tip.js';
import { setCompanyTextures } from '../utils/company-textures.js';
import { samplesFrom, sampleProfileSvg, SAMPLE_PROFILE_HEIGHT } from '../utils/texture-sample.js';
import './texture-sample-dialog.js';
import {
  TEXTURE_CATALOG, TEXTURE_PATTERNS, TEXTURE_GROUP_LABELS, getPatternDef, texturePatternSvg,
} from '@mv/models';

/**
 * Bedrijfspagina (/settings/textures): kleur, patroon en markeringkleur per grondsoort, BZB,
 * vulmateriaal (tab Afwerking) en peilbuisonderdeel (tab Peilbuis) in het boorprofiel. Afwerking
 * en peilbuis gelden voor beide normen. Standaard volgt alles NEN 6693 Figuur 1 (zie @mv/models textures.ts);
 * hier legt het bedrijf alleen vast waar het daarvan afwijkt. Iedereen in het bedrijf mag
 * kijken, een eigenaar, manager of sysadmin mag aanpassen.
 */

// De norm staat bovenaan als eigen keuze: alles eronder (tabel, voorbeeld) hoort bij die norm.
const STANDARDS = [
  { id: 'nen5104', label: 'NEN 5104' },
  { id: 'nen6693', label: 'NEN 6693' },
];

/** Uitleg bij de tab BZBs (tegel met hover-tekst). */
const BZB_UITLEG = 'Een BZB wordt pas in het boorprofiel getoond als je hem een kleur en/of patroon geeft.\n\n'
  + 'Is een BZB in een laag als volledig of dominant opgegeven, dan bepaalt hij de textuur van de hele laag. '
  + 'Anders wordt hij getoond als een bijmenging: een smalle balk rechts in de laag.';

/** Tabs; Afwerking en Peilbuis staan los van de norm. */
const TABS = [
  { id: 'soil', label: 'Grondsoorten' },
  { id: 'bzb', label: 'BZBs' },
  { id: 'afwerking', label: 'Afwerking', allNorms: true },
  { id: 'peilbuis', label: 'Peilbuis', allNorms: true },
];
const inTab = (e, tab, standard) => e.category === tab && (TABS.find((t) => t.id === tab)?.allNorms ? e.standard === null : e.standard === standard);

const sameTexture = (a, b) => !!a && !!b && a.fill === b.fill && a.pattern === b.pattern && a.mark === b.mark && !!a.warn === !!b.warn;

/** Een id die veilig in `url(#...)` staat. */
const patternId = (key) => `tx-${key.replace(/[^a-zA-Z0-9_-]/g, '_')}`;

/** Toelichting onder de naam: waarom een code dit symbool heeft, of wat er in een reeks zit. */
function noteFor(e) {
  if (e.codes.length > 1) {
    const more = e.memberLabels.length > 4 ? ', …' : '';
    return `${e.codes.length} codes: ${e.memberLabels.slice(0, 4).join(', ')}${more}`;
  }
  if (e.category !== 'soil') return '';
  if (e.standard === 'nen5104' && e.codes[0] === 'S') return 'let op: in NEN 6693 is S silt';
  if (e.standard === 'nen5104' && e.codes[0] === 'L') return 'volgt silt uit Figuur 1';
  if (e.family === 'veen' && e.codes[0] !== 'V') return 'organisch: symbool van veen';
  if (e.family === 'bijzonder') return 'geen symbool in Figuur 1';
  return '';
}

export class TextureSettings extends LitElement {
  static properties = {
    _loading: { state: true },
    _error: { state: true },
    _canEdit: { state: true },
    _tab: { state: true },
    _standard: { state: true },
    _query: { state: true },
    _onlyDev: { state: true },
    _draft: { state: true },
    _saved: { state: true },
    _saving: { state: true },
    _flash: { state: true },
    _samples: { state: true },     // voorbeeldprofiel per norm, van het bedrijf (utils/texture-sample.js)
    _sampleError: { state: true },
    _editSample: { state: true },  // modal 'Voorbeeld aanpassen' open
    _viewportH: { state: true },   // vensterhoogte: bovengrens voor het voorbeeld
    _patternPick: { state: true }, // open patroonkiezer: { key, top, left } of null
  };

  constructor() {
    super();
    this._loading = true;
    this._error = null;
    this._canEdit = false;
    this._tab = 'soil';
    this._standard = 'nen5104'; // de meest gebruikte norm opent eerst
    this._query = '';
    this._onlyDev = false;
    this._draft = {};
    this._saved = {};
    this._saving = false;
    this._flash = false;
    this._samples = samplesFrom(null);
    this._sampleError = null;
    this._editSample = false;
  }

  connectedCallback() {
    super.connectedCallback();
    this._load();
    // Het voorbeeld stond tot oktober 2026 in de browser (en werd zo tussen bedrijven gedeeld);
    // het staat nu bij het bedrijf. De oude kopieën opruimen.
    try { localStorage.removeItem('mv.textures.sample.v1'); localStorage.removeItem('mv.textures.sample.v2'); } catch (_) { /* geen opslag */ }
    this._viewportH = window.innerHeight;
    this._onResize = () => { this._viewportH = window.innerHeight; this._patternPick = null; };
    window.addEventListener('resize', this._onResize);
    // De patroonkiezer staat vast op het scherm; bij scrollen zou hij los van zijn rij hangen.
    this._onScroll = () => { if (this._patternPick) this._patternPick = null; };
    window.addEventListener('scroll', this._onScroll, true);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    clearTimeout(this._flashTimer);
    window.removeEventListener('resize', this._onResize);
    window.removeEventListener('scroll', this._onScroll, true);
  }

  async _load() {
    this._loading = true;
    try {
      const res = await api.getCompanyTextures();
      this._saved = res?.overrides || {};
      this._draft = { ...this._saved };
      this._canEdit = !!res?.canEdit;
      this._samples = samplesFrom(res?.sample);
    } catch (e) {
      this._error = 'Laden mislukt';
    } finally {
      this._loading = false;
    }
  }

  _current(e) { return this._draft[e.key] || e.defaultTexture; }

  _setField(e, field, value) {
    const next = { ...this._current(e), [field]: value };
    const draft = { ...this._draft };
    if (sameTexture(next, e.defaultTexture)) delete draft[e.key]; else draft[e.key] = next;
    this._draft = draft;
    this._flash = false;
  }

  _reset(e) {
    const draft = { ...this._draft };
    delete draft[e.key];
    this._draft = draft;
    this._flash = false;
  }

  get _changedCount() {
    const keys = new Set([...Object.keys(this._draft), ...Object.keys(this._saved)]);
    return [...keys].filter((k) => !sameTexture(this._draft[k], this._saved[k])).length;
  }

  async _save() {
    this._saving = true; this._error = null;
    try {
      const res = await api.updateCompanyTextures(this._draft);
      this._saved = res?.overrides || {};
      this._draft = { ...this._saved };
      setCompanyTextures(this._saved);
      this._flash = true;
      clearTimeout(this._flashTimer);
      this._flashTimer = setTimeout(() => { this._flash = false; }, 2500);
    } catch (e) {
      this._error = 'Opslaan mislukt';
    } finally {
      this._saving = false;
    }
  }

  // ─── Tekenen ─────────────────────────────────────────────────────────────

  /** Een rechthoek in de textuur, als inline SVG. `scale` vergroot het patroon. */
  _swatch(id, texture, w, h, scale) {
    return html`<svg width=${w} height=${h} viewBox="0 0 ${w / scale} ${h / scale}" preserveAspectRatio="none" aria-hidden="true">
      <defs>${unsafeSVG(texturePatternSvg(id, texture))}</defs>
      <rect width=${w / scale} height=${h / scale} fill="url(#${id})"></rect>
    </svg>`;
  }

  _row(e) {
    const t = this._current(e);
    const isDev = !!this._draft[e.key];
    const note = noteFor(e);
    const patternLabel = getPatternDef(t.pattern).label;
    return html`
      <div class="row grid ${e.category === 'bzb' ? 'with-warn' : ''} ${isDev ? 'dev' : ''}">
        <div class="swatch">${e.colorOnly
          ? html`<svg width="56" height="30" aria-hidden="true"><rect width="56" height="30" fill="#fff"></rect><line x1="4" y1="15" x2="52" y2="15" stroke=${t.fill} stroke-width="2.5"></line></svg>`
          : this._swatch(patternId(e.key), t, 56, 30, 1.6)}</div>
        <div class="code" title=${e.codes.join(', ')}>${e.codes.length > 1 ? 'alle' : e.codes[0].replace(/^VUL_/, '')}</div>
        <div class="name">
          <div>${e.label.charAt(0).toUpperCase() + e.label.slice(1)}</div>
          ${note ? html`<div class="note">${note}</div>` : ''}
          ${isDev ? html`
            <div class="dev-line">
              <span class="badge" title=${e.category === 'soil' ? 'Afwijkend van NEN 6693 Figuur 1' : 'Afwijkend van de standaard'}>Aangepast</span>
              ${this._canEdit ? html`<button class="reset" title="Terug naar de textuur uit de norm" @click=${() => this._reset(e)}>terug naar norm</button>` : ''}
            </div>` : ''}
        </div>
        ${e.colorOnly ? html`
          ${this._canEdit ? html`
            <label class="picker">
              <input type="color" .value=${t.fill} @input=${(ev) => this._setField(e, 'fill', ev.target.value)} aria-label="Kleur van de lijn">
              <span class="hex">${t.fill}</span>
            </label>` : html`<div class="hex">${t.fill}</div>`}
          <div class="hex">—</div>
          <div class="ro">Lijn</div>
        ` : this._canEdit ? html`
          <label class="picker">
            <input type="color" .value=${t.fill} @input=${(ev) => this._setField(e, 'fill', ev.target.value)} aria-label="Achtergrondkleur">
            <span class="hex">${t.fill}</span>
          </label>
          <!-- Ook zonder patroon in te stellen: dan staat de kleur klaar voor het patroon dat je kiest. -->
          <label class="picker">
            <input type="color" .value=${t.mark} @input=${(ev) => this._setField(e, 'mark', ev.target.value)} aria-label="Markeringkleur">
            <span class="hex">${t.mark}</span>
          </label>
          <button class="pattern-btn ${this._patternPick?.key === e.key ? 'open' : ''}" title=${patternLabel}
            aria-label="Patroon: ${patternLabel}" aria-haspopup="dialog" @click=${(ev) => this._openPatternPick(ev, e)}>
            ${this._swatch(patternId(`btn-${e.key}`), t, 44, 22, 1.4)}
            <span class="pattern-name">${patternLabel}</span>
          </button>
        ` : html`
          <div class="hex">${t.fill}</div>
          <div class="hex">${t.mark}</div>
          <div class="ro">${patternLabel}</div>
        `}
        ${e.category === 'bzb' ? html`
          <label class="warn-cell" title="Waarschuwingsdriehoek in het boorprofiel bij elke laag met deze BZB">
            <input type="checkbox" .checked=${!!t.warn} ?disabled=${!this._canEdit}
              @change=${(ev) => this._setField(e, 'warn', ev.target.checked)} aria-label="Waarschuwing bij ${e.label}">
            ${t.warn ? html`<span class="warn-icon" aria-hidden="true">⚠</span>` : ''}
          </label>` : ''}
      </div>`;
  }

  // ─── Patroonkiezer ─────────────────────────────────────────────────────────

  /**
   * Een popup met alle patronen als plaatje, in de kleuren van deze rij: je kiest wat je ziet in
   * plaats van een naam uit een lijst. Vast gepositioneerd onder (of boven) de knop.
   */
  _openPatternPick(ev, e) {
    ev.stopPropagation();
    if (this._patternPick?.key === e.key) { this._patternPick = null; return; }
    const r = ev.currentTarget.getBoundingClientRect();
    const W = 412, H = Math.min(420, 64 + Math.ceil(TEXTURE_PATTERNS.length / 5) * 58);
    const below = r.bottom + 6 + H < window.innerHeight;
    this._patternPick = {
      key: e.key,
      top: below ? r.bottom + 6 : Math.max(8, r.top - 6 - H),
      left: Math.max(8, Math.min(r.left, window.innerWidth - W - 8)),
    };
  }

  _renderPatternPick() {
    const pick = this._patternPick;
    if (!pick) return '';
    const e = TEXTURE_CATALOG.find((x) => x.key === pick.key);
    if (!e) return '';
    const t = this._current(e);
    const choose = (id) => { this._setField(e, 'pattern', id); this._patternPick = null; };
    return html`
      <div class="pp-backdrop" @click=${() => { this._patternPick = null; }}></div>
      <div class="pp" role="dialog" aria-label="Kies een patroon" style="top:${pick.top}px; left:${pick.left}px;"
        @keydown=${(ev) => { if (ev.key === 'Escape') this._patternPick = null; }}>
        <div class="pp-head">Patroon voor <b>${e.label}</b></div>
        <div class="pp-grid">
          ${TEXTURE_PATTERNS.map((p) => {
            const sample = { ...t, pattern: p.id };
            return html`
              <button class="pp-item ${p.id === t.pattern ? 'on' : ''}" title=${p.label} @click=${() => choose(p.id)}>
                ${this._swatch(patternId(`pp-${e.key}-${p.id}`), sample, 56, 28, 1.4)}
                <span>${p.label}</span>
              </button>`;
          })}
        </div>
      </div>`;
  }

  _visibleGroups() {
    const q = this._query.trim().toLowerCase();
    const rows = TEXTURE_CATALOG.filter((e) => inTab(e, this._tab, this._standard));
    const matches = (e) => !q || e.label.toLowerCase().includes(q)
      || e.codes.some((c) => c.toLowerCase().includes(q))
      || e.memberLabels.some((l) => l.toLowerCase().includes(q));
    const visible = rows.filter((e) => matches(e) && (!this._onlyDev || this._draft[e.key]));
    const devCount = rows.filter((e) => this._draft[e.key]).length;
    if (this._tab === 'soil') return { groups: visible.length ? [{ name: '', rows: visible }] : [], devCount };
    // BZB: per familie, in de volgorde van de groepnamen.
    const order = Object.keys(TEXTURE_GROUP_LABELS);
    const byFamily = new Map();
    for (const e of visible) {
      if (!byFamily.has(e.family)) byFamily.set(e.family, []);
      byFamily.get(e.family).push(e);
    }
    const groups = [...byFamily.entries()]
      .sort(([a], [b]) => order.indexOf(a) - order.indexOf(b))
      .map(([family, list]) => ({ name: TEXTURE_GROUP_LABELS[family], rows: list }));
    return { groups, devCount };
  }

  /**
   * Het voorbeeld: hetzelfde boorprofiel als in 'Voorbeeld aanpassen' (sampleProfileSvg), op ware
   * grootte. Even hoog als daar, behalve als het venster te laag is: het voorbeeld staat vast
   * naast de tabel en moet in één keer te zien zijn. ~350 px gaat op aan de kop van de kaart,
   * de kop en voetregel van de tekening en de uitleg eronder.
   */
  _preview() {
    const maxH = Math.max(240, (this._viewportH || 800) - 350);
    const svg = sampleProfileSvg(this._samples[this._standard], this._standard, this._draft, Math.min(SAMPLE_PROFILE_HEIGHT, maxH));
    return svg ? html`<div class="pv-svg">${unsafeSVG(svg)}</div>` : html`<div class="pv-empty">Geen lagen.</div>`;
  }

  /** Opslaan bij het bedrijf (alleen een beheerder; de server controleert dat ook). */
  async _saveSample(sample) {
    if (!this._canEdit) return;
    const next = { ...this._samples, [this._standard]: sample };
    this._sampleError = null;
    try {
      await api.updateTextureSample(next);
      this._samples = next;
      this._editSample = false;
    } catch (e) {
      this._sampleError = 'Voorbeeld opslaan mislukt';
    }
  }

  render() {
    if (this._loading) return html`<div class="page"><p class="muted">Laden…</p></div>`;
    const { groups, devCount } = this._visibleGroups();
    const count = (category) => TEXTURE_CATALOG.filter((e) => inTab(e, category, this._standard)).length;
    const allNorms = TABS.find((t) => t.id === this._tab)?.allNorms;
    const changed = this._changedCount;

    return html`
      <div class="page">
        <div class="head">
          <div class="intro">
            <h1>Texturen</h1>
            <p class="subtitle">Bepaal per standaard de kleur en arcering van elke grondsoort en BZB in het boorprofiel. Dit geldt ook voor de PDF-export en de field-recorder.</p>
          </div>
          ${this._canEdit ? html`
            <div class="actions">
              ${this._error ? html`<span class="err">${this._error}</span>` : ''}
              ${this._flash ? html`<span class="ok">Opgeslagen ✓</span>` : ''}
              ${changed ? html`<span class="dirty">${changed === 1 ? '1 textuur gewijzigd' : `${changed} texturen gewijzigd`}</span>` : ''}
              <button class="save" ?disabled=${!changed || this._saving} @click=${this._save}>${this._saving ? 'Opslaan…' : 'Opslaan'}</button>
            </div>
          ` : ''}
        </div>

        ${!this._canEdit ? html`<div class="readonly">Je kunt de texturen bekijken. Alleen een eigenaar, manager of sysadmin kan ze aanpassen.</div>` : ''}
        ${!this._canEdit && this._error ? html`<p class="err">${this._error}</p>` : ''}

        <div class="norm">
          <label for="norm-select">Norm</label>
          <select id="norm-select" .value=${this._standard} @change=${(ev) => { this._standard = ev.target.value; }}>
            ${STANDARDS.map((s) => html`<option value=${s.id} ?selected=${this._standard === s.id}>${s.label}</option>`)}
          </select>
        </div>

        <div class="layout">
          <section class="card">
            <div class="tabbar" role="tablist">
              ${TABS.map((t) => html`
                <button class="tab ${this._tab === t.id ? 'on' : ''}" role="tab" aria-selected=${this._tab === t.id}
                  @click=${() => { this._tab = t.id; this._query = ''; }}>
                  ${t.label}<span class="count">${count(t.id)}</span>
                </button>`)}
              <div class="spacer"></div>
              ${allNorms
                ? html`<span class="norm-chip">geldt voor <b>alle normen</b></span>`
                : html`<span class="norm-chip">volgens <b>${STANDARDS.find((s) => s.id === this._standard)?.label}</b></span>`}
            </div>
            <div class="tabhead">
              <input class="search" type="search" placeholder="Zoek in ${this._tab === 'bzb' ? 'BZBs' : (TABS.find((t) => t.id === this._tab)?.label || '').toLowerCase()}"
                .value=${this._query} @input=${(ev) => { this._query = ev.target.value; }}>
              <button class="only-dev ${this._onlyDev ? 'on' : ''}" aria-pressed=${this._onlyDev} @click=${() => { this._onlyDev = !this._onlyDev; }}>
                Alleen afwijkend <span class="count">${devCount}</span>
              </button>
              ${this._tab === 'bzb' ? html`
                <span class="info-tile" data-tip=${BZB_UITLEG} @mousemove=${tipMove} @mouseleave=${tipHide}>
                  ${unsafeSVG(lucideIcon(Info, 14))} Hoe worden BZBs getoond?
                </span>` : ''}
            </div>

            <div class="scroll">
              <div class="table">
                <div class="grid th ${this._tab === 'bzb' ? 'with-warn' : ''}">
                  <div>Textuur</div><div>Code</div><div>Naam</div><div>Achtergrond</div><div>Markering</div><div>Patroon</div>
                  ${this._tab === 'bzb' ? html`<div class="warn-head">Waarschuwing</div>` : ''}
                </div>
                ${groups.map((g) => html`
                  ${g.name ? html`<div class="group"><span class="group-name">${g.name}</span><span class="group-count">${g.rows.length}</span></div>` : ''}
                  ${g.rows.map((e) => this._row(e))}
                `)}
                ${!groups.length ? html`<div class="empty">${this._query ? `Geen texturen gevonden voor “${this._query}”.` : 'Geen afwijkende texturen.'}</div>` : ''}
              </div>
            </div>
          </section>

          <aside class="card preview">
            ${this._canEdit ? html`
              <button class="edit-link pv-edit" title="Voorbeeld aanpassen" @click=${() => { this._editSample = true; }}>
                ${unsafeSVG(lucideIcon(Edit2, 12))} Aanpassen
              </button>` : ''}
            ${this._preview()}
            <div class="legend">
              <div class="legend-item"><div class="legend-box full"></div><div>Laag bestaat uit een BZB: hele vak in de BZB-textuur (standaard wit).</div></div>
              <div class="legend-item"><div class="legend-box"><div class="legend-bar"></div></div><div>BZB als bijmenging: smalle balk rechts, alleen als je de BZB een kleur of patroon geeft.</div></div>
            </div>
          </aside>
        </div>

        ${this._renderPatternPick()}

        <texture-sample-dialog .open=${this._editSample} .standard=${this._standard}
          .sample=${this._samples[this._standard]} .textures=${this._draft} .error=${this._sampleError}
          @close=${() => { this._editSample = false; this._sampleError = null; }}
          @save=${(e) => this._saveSample(e.detail.sample)}></texture-sample-dialog>
      </div>
    `;
  }

  static styles = css`
    :host { display: block; }
    .page { max-width: 1440px; }
    .muted { color: var(--mv-text-muted); }

    .head { display: flex; align-items: flex-end; gap: 16px; flex-wrap: wrap; margin-bottom: 22px; }
    .intro { flex: 1 1 420px; min-width: 0; }
    h1 { margin: 0 0 6px; font-size: 1.5rem; font-weight: 700; letter-spacing: -0.03em; color: var(--mv-text); }
    .subtitle { margin: 0; font-size: 0.875rem; line-height: 1.55; color: var(--mv-text-sub); max-width: 42rem; text-wrap: pretty; }
    .actions { display: flex; align-items: center; gap: 14px; }
    .ok { color: var(--mv-primary); font-weight: 600; font-size: 0.875rem; }
    .err { color: var(--mv-danger, #DC2626); font-weight: 600; font-size: 0.875rem; }
    .dirty { color: var(--mv-text-sub); font-size: var(--mv-text-sm, 0.78125rem); }
    button.save {
      background: var(--mv-primary); color: #fff; border: none; border-radius: 8px;
      padding: 10px 22px; font-weight: 700; font-size: 0.875rem; font-family: inherit; cursor: pointer;
    }
    button.save:hover:not(:disabled) { background: var(--mv-primary-hover, #3d812b); }
    button.save:disabled { opacity: 0.55; cursor: default; }

    .readonly {
      margin-bottom: 16px; padding: 10px 14px; border-radius: 9px; font-size: 0.8125rem;
      border: 1px solid var(--mv-warning-panel-border, #E9D5A8); background: var(--mv-warning-panel-bg, #FAEDD2); color: var(--mv-warning-text, #8A5B10);
    }

    .norm { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; margin-bottom: 18px; }
    .norm label { font-size: 0.875rem; font-weight: 600; color: var(--mv-text); }
    .norm select {
      appearance: none; -webkit-appearance: none; min-width: 200px; padding: 8px 2.2rem 8px 12px;
      border: 1.5px solid var(--mv-primary); border-radius: 8px; background-color: var(--mv-surface); color: var(--mv-text);
      font: inherit; font-size: 0.875rem; font-weight: 600; cursor: pointer;
      background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%23181C32' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E");
      background-repeat: no-repeat; background-position: right 0.7rem center; background-size: 1rem;
    }
    .norm select:focus { outline: none; box-shadow: 0 0 0 3px color-mix(in srgb, var(--mv-primary) 18%, transparent); }
    .norm-chip {
      padding: 4px 9px; border-radius: 6px; font-size: 0.72rem; color: var(--mv-primary);
      background: color-mix(in srgb, var(--mv-primary) 10%, transparent);
    }
    .layout { display: flex; gap: 24px; align-items: flex-start; flex-wrap: wrap; }
    .card { background: var(--mv-surface); border: 1px solid var(--mv-border); border-radius: 14px; box-shadow: 0 5px 15px rgba(0,0,0,0.02); }
    /* Tabel en voorbeeld naast elkaar; past dat niet, dan het voorbeeld eronder in plaats van een
       tabel die zijn kolommen kwijtraakt. */
    section.card { flex: 1 1 680px; min-width: 0; }

    /* Tabbalk: onderstreept, met tellertje; het tellertje van de actieve tab kleurt mee. */
    .tabbar { display: flex; align-items: center; gap: 4px; padding: 0 16px; border-bottom: 1px solid var(--mv-border); flex-wrap: wrap; }
    .tab {
      display: inline-flex; align-items: center; gap: 7px; background: none; border: none; cursor: pointer;
      padding: 14px 10px 12px; margin-bottom: -1px; border-bottom: 2px solid transparent;
      font: inherit; font-size: 0.875rem; font-weight: 600; color: var(--mv-text-sub);
    }
    .tab:hover { color: var(--mv-text); }
    .tab.on { color: var(--mv-text); border-bottom-color: var(--mv-primary); }
    .count {
      font-size: 0.6875rem; font-weight: 700; color: var(--mv-text-muted); background: var(--mv-surface-alt, #F3F4F6);
      border-radius: 999px; padding: 1px 7px;
    }
    .tab.on .count { color: var(--mv-primary); background: color-mix(in srgb, var(--mv-primary) 12%, transparent); }
    .spacer { flex: 1; }
    .tabhead { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; padding: 14px 16px; border-bottom: 1px solid var(--mv-border-soft); }
    .info-tile {
      margin-left: auto; display: inline-flex; align-items: center; gap: 6px; padding: 7px 12px; border-radius: 8px; cursor: help;
      font-size: 0.8125rem; font-weight: 600; color: var(--mv-text-sub);
      background: var(--mv-surface-alt, #F3F4F6); border: 1px solid var(--mv-border);
    }
    .info-tile:hover { color: var(--mv-text); border-color: var(--mv-text-muted); }
    .only-dev {
      display: inline-flex; align-items: center; gap: 6px; padding: 7px 12px; border: 1px solid var(--mv-border); border-radius: 8px;
      background: var(--mv-surface); font: inherit; font-size: 0.8125rem; font-weight: 600; color: var(--mv-text); cursor: pointer;
    }
    .only-dev.on { border-color: var(--mv-primary); color: var(--mv-primary); }
    .search {
      width: 210px; max-width: 100%; padding: 8px 12px; border: 1px solid var(--mv-border); border-radius: 8px;
      background: var(--mv-bg); color: var(--mv-text); font: inherit; font-size: 0.8125rem; outline: none;
    }
    .search:focus { border-color: var(--mv-primary); background: var(--mv-surface); }

    .scroll { overflow-x: auto; }
    .table { min-width: 640px; }
    .grid { display: grid; grid-template-columns: 60px 88px minmax(150px, 1fr) 124px 108px 158px; gap: 12px; align-items: center; }
    .grid.with-warn { grid-template-columns: 60px 88px minmax(140px, 1fr) 124px 108px 158px 104px; }
    .warn-head { text-align: center; }
    .warn-cell { display: flex; align-items: center; justify-content: center; gap: 6px; cursor: pointer; }
    .warn-cell input { width: 16px; height: 16px; margin: 0; accent-color: var(--mv-primary); cursor: pointer; }
    .warn-cell input:disabled { cursor: default; }
    .warn-icon { color: #e0a100; font-size: 0.95rem; line-height: 1; }
    .th {
      padding: 10px 16px; border-bottom: 1px solid var(--mv-border-soft);
      font-size: 0.6875rem; font-weight: 700; letter-spacing: 0.07em; text-transform: uppercase; color: var(--mv-text-sub);
    }
    .group { display: flex; align-items: baseline; gap: 8px; padding: 16px 16px 6px; }
    .group-name { font-size: 0.8125rem; font-weight: 700; color: var(--mv-text); }
    .group-count { font-size: var(--mv-text-sm, 0.78125rem); color: var(--mv-text-muted); }

    .row { padding: 9px 16px; border-bottom: 1px solid var(--mv-border-soft); background: var(--mv-surface); }
    /* Aangepast: amber streep links + lichte achtergrond, en het label onder de naam -- zo zie je
       het ook als de tabel smal is (een kolom rechts viel buiten beeld). */
    .row.dev {
      background: color-mix(in srgb, var(--mv-warning-panel-bg, #FAEDD2) 45%, var(--mv-surface));
      box-shadow: inset 3px 0 0 var(--mv-warning, #D97706);
    }
    .dev-line { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; margin-top: 5px; }
    .swatch svg { display: block; border-radius: 5px; border: 1px solid #d6d9dd; box-sizing: border-box; }
    .code { font-family: 'JetBrains Mono', ui-monospace, monospace; font-size: var(--mv-text-sm, 0.78125rem); font-weight: 600; color: var(--mv-text); }
    .name { min-width: 0; font-size: 0.8125rem; font-weight: 600; color: var(--mv-text); }
    .note { font-size: 0.6875rem; font-weight: 400; color: var(--mv-text-muted); margin-top: 2px; }
    .picker {
      display: flex; align-items: center; gap: 8px; padding: 5px 8px; border: 1px solid var(--mv-border); border-radius: 8px;
      background: var(--mv-bg); cursor: pointer;
    }
    .picker input[type=color] { -webkit-appearance: none; appearance: none; width: 22px; height: 22px; padding: 0; border: none; background: none; cursor: pointer; }
    .picker input[type=color]::-webkit-color-swatch-wrapper { padding: 0; }
    .picker input[type=color]::-webkit-color-swatch { border: 1px solid rgba(0,0,0,.15); border-radius: 5px; }
    .picker input[type=color]::-moz-color-swatch { border: 1px solid rgba(0,0,0,.15); border-radius: 5px; }
    .picker input[type=color]:disabled { cursor: default; }
    .hex { font-family: 'JetBrains Mono', ui-monospace, monospace; font-size: 0.75rem; color: var(--mv-text-sub); }
    .ro { font-size: 0.8125rem; color: var(--mv-text-sub); }
    /* Patroon: knop met het patroon als plaatje; klik = popup met alle patronen. */
    .pattern-btn {
      display: flex; align-items: center; gap: 8px; width: 100%; padding: 4px 8px 4px 4px; border: 1px solid var(--mv-border);
      border-radius: 8px; background: var(--mv-bg); cursor: pointer; font: inherit; text-align: left; min-width: 0;
    }
    .pattern-btn:hover, .pattern-btn.open { border-color: var(--mv-primary); }
    .pattern-btn svg { flex: none; display: block; border-radius: 4px; border: 1px solid #d6d9dd; box-sizing: border-box; }
    .pattern-name { font-size: 0.75rem; color: var(--mv-text-sub); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
    .pp-backdrop { position: fixed; inset: 0; z-index: 1000; }
    .pp {
      position: fixed; z-index: 1001; width: 412px; max-height: 420px; overflow-y: auto; box-sizing: border-box; padding: 12px; border-radius: 12px;
      background: var(--mv-surface); border: 1px solid var(--mv-border); box-shadow: 0 12px 32px rgba(0,0,0,0.16);
    }
    .pp-head { font-size: 0.75rem; color: var(--mv-text-sub); margin: 0 2px 10px; }
    .pp-head b { color: var(--mv-text); }
    .pp-grid { display: grid; grid-template-columns: repeat(5, 1fr); gap: 6px; }
    .pp-item {
      display: flex; flex-direction: column; align-items: center; gap: 4px; padding: 6px 4px; border: 1.5px solid transparent;
      border-radius: 8px; background: none; cursor: pointer; font: inherit;
    }
    .pp-item:hover { background: var(--mv-surface-alt, #F3F4F6); }
    .pp-item.on { border-color: var(--mv-primary); background: color-mix(in srgb, var(--mv-primary) 8%, transparent); }
    .pp-item svg { display: block; border-radius: 4px; border: 1px solid #b8b8b8; box-sizing: border-box; }
    .pp-item span { font-size: 0.625rem; line-height: 1.2; color: var(--mv-text-sub); text-align: center; }
    .select {
      appearance: none; -webkit-appearance: none; width: 100%; padding: 8px 2rem 8px 10px; border: 1px solid var(--mv-border); border-radius: 8px;
      background-color: var(--mv-bg); color: var(--mv-text); font: inherit; font-size: 0.8125rem; cursor: pointer;
      background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%238B978D' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E");
      background-repeat: no-repeat; background-position: right 0.6rem center; background-size: 1rem;
    }
    .badge {
      display: inline-flex; align-items: center; padding: 2px 8px; border-radius: 999px; white-space: nowrap;
      background: var(--mv-warning-panel-bg, #FAEDD2); color: var(--mv-warning-text, #8A5B10); font-size: 0.6875rem; font-weight: 700; letter-spacing: 0.02em;
    }
    button.reset {
      border: none; background: none; padding: 0; color: var(--mv-text-sub); font-family: inherit;
      font-size: 0.6875rem; font-weight: 600; cursor: pointer; white-space: nowrap; text-decoration: underline; text-underline-offset: 2px;
    }
    button.reset:hover { color: var(--mv-text); }
    .empty { padding: 40px 16px; text-align: center; font-size: 0.8125rem; color: var(--mv-text-muted); }

    /* Breed genoeg voor het profiel op ware grootte (~445 px zonder legenda). */
    aside.preview { flex: 0 1 478px; min-width: 300px; position: sticky; top: 20px; padding: 16px; box-sizing: border-box; }
    /* Zelfde knop als 'Bewerken' bij een boring: groen tekstlinkje met potlood, rechtsboven naast
       de kop van de tekening. */
    .edit-link {
      background: none; border: none; cursor: pointer; color: var(--mv-primary, #10B981);
      font-family: inherit; font-size: 0.75rem; font-weight: 600; display: inline-flex; align-items: center; gap: 4px; padding: 0;
    }
    .edit-link:hover { text-decoration: underline; }
    .pv-edit { position: absolute; top: 30px; right: 30px; }
    .pv-svg svg { display: block; }
    .pv-empty { grid-column: 1 / -1; font-size: 0.75rem; color: var(--mv-text-muted); }
    .legend { border-top: 1px solid var(--mv-border-soft); margin-top: 14px; padding-top: 12px; display: grid; gap: 8px; font-size: 0.71875rem; color: var(--mv-text-sub); line-height: 1.45; }
    .legend-item { display: flex; gap: 8px; align-items: flex-start; }
    .legend-box { width: 14px; height: 14px; flex: none; border: 1px solid #c8c8c8; display: flex; justify-content: flex-end; box-sizing: border-box; }
    .legend-box.full { background: #E7E5DF; }
    .legend-bar { width: 4px; background: var(--mv-text-sub); }
  `;
}

customElements.define('texture-settings', TextureSettings);
